import React, { useMemo, useEffect, useState } from 'react';
import { CurrencyDollarIcon } from '@heroicons/react/outline';

// Calendar component for date picking
const Calendar = ({ selectedDate, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const isSelected = (date) => {
    return selectedDate && formatDate(date) === selectedDate;
  };
  
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  const days = getDaysInMonth(currentMonth);
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          ←
        </button>
        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          →
        </button>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} className="h-10"></div>;
          }
          
          const isPast = isPastDate(day);
          const isTodayDate = isToday(day);
          const isSelectedDate = isSelected(day);
          
          return (
            <button
              key={index}
              onClick={() => !isPast && onDateSelect(formatDate(day))}
              disabled={isPast}
              className={`
                h-10 w-full rounded-lg text-sm font-medium transition-colors
                ${isPast 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'hover:bg-orange-100 cursor-pointer'
                }
                ${isTodayDate 
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-300' 
                  : ''
                }
                ${isSelectedDate 
                  ? 'bg-orange-500 text-white hover:bg-orange-600' 
                  : ''
                }
                ${!isPast && !isTodayDate && !isSelectedDate 
                  ? 'text-gray-700' 
                  : ''
                }
              `}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-xs text-gray-600">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-100 border-2 border-blue-300 rounded mr-1"></div>
          Today
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-orange-500 rounded mr-1"></div>
          Selected
        </div>
      </div>
    </div>
  );
};

const DynamicField = ({ 
  fieldName, 
  fieldConfig, 
  value, 
  onChange, 
  error,
  className = "" 
}) => {
  // Memoize object URLs for File objects in conditional-file-upload fields
  const imagePreviews = useMemo(() => {
    if (fieldConfig.type !== 'conditional-file-upload' || !value?.files) return [];
    return value.files.map(file => {
      if (typeof file === 'string') {
        return file;
      } else if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      return null;
    });
  }, [fieldConfig.type, value?.files]);

  // Cleanup object URLs on unmount or files change
  useEffect(() => {
    return () => {
      if (fieldConfig.type !== 'conditional-file-upload' || !value?.files) return;
      value.files.forEach(file => {
        if (file instanceof File) {
          const url = URL.createObjectURL(file);
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [fieldConfig.type, value?.files]);

  const handleChange = (newValue) => {
    onChange(fieldName, newValue);
  };

  const renderField = () => {
    switch (fieldConfig.type) {
      case 'textarea':
        return (
          <textarea
            placeholder={fieldConfig.placeholder}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            rows={4}
            className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none ${className}`}
          />
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors ${className}`}
          >
            <option value="">Select {fieldConfig.label}</option>
            {fieldConfig.options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        // Special styling for frequency field
        if (fieldName === 'frequency') {
          return (
            <div className="space-y-3 sm:space-y-4">
              {fieldConfig.options.map((option, index) => {
                const icon = option.value === 'one-time' ? '📦' : '🔄';
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleChange(option.value)}
                    className={`w-full flex items-center p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                      value === option.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <span className="text-xl sm:text-2xl mr-3">{icon}</span>
                    <div>
                      <span className="font-medium text-sm sm:text-base">{option.label}</span>
                      <div className="text-xs sm:text-sm text-gray-600">
                        {option.value === 'one-time' ? 'Single order for this requirement' : 'Regular orders on a schedule'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        }
        
        // Default radio styling for other fields
        return (
          <div className="space-y-3">
            {fieldConfig.options.map((option, index) => (
              <label key={index} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={fieldName}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleChange(e.target.value)}
                  className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-3 text-gray-700 text-sm sm:text-base">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors ${className}`}
          />
        );

      case 'input':
        return (
          <input
            type="text"
            placeholder={fieldConfig.placeholder}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors ${className}`}
          />
        );

      case 'budget':
        return (
          <div className="space-y-4">
            <div className="relative">
              <CurrencyDollarIcon className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="e.g., ₦50,000 - ₦70,000"
                value={value || ''}
                onChange={(e) => handleChange(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
              />
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3 sm:mb-4">Or choose a budget range:</p>
              <div className="space-y-3">
                {[
                  { value: "low", label: "Low", range: "₦10,000 - ₦50,000" },
                  { value: "medium", label: "Medium", range: "₦50,000 - ₦200,000" },
                  { value: "premium", label: "Premium", range: "₦200,000+" }
                ].map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleChange(option.range)}
                    className={`w-full flex items-center justify-between p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                      value === option.range
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <span className="font-medium text-sm sm:text-base">{option.label}</span>
                    <span className="text-gray-500 text-sm sm:text-base">{option.range}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'urgency':
        return (
          <div className="space-y-3 sm:space-y-4">
            {[
              { value: "urgent", label: "Urgent (Within 24 hours)", icon: "🚨" },
              { value: "this_week", label: "This Week", icon: "📅" },
              { value: "custom", label: "Choose date", icon: "📆" }
            ].map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleChange(option.value)}
                className={`w-full flex items-center p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                  value === option.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                <span className="text-xl sm:text-2xl mr-3">{option.icon}</span>
                <span className="font-medium text-sm sm:text-base">{option.label}</span>
              </button>
            ))}
            
            {(value === 'custom' || (value !== 'urgent' && value !== 'this_week' && value !== 'custom' && value)) && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {value === 'custom' ? 'Select your preferred date:' : 'Selected date:'}
                </label>
                {value !== 'custom' && value !== 'urgent' && value !== 'this_week' && (
                  <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-orange-800">
                        📅 {new Date(value).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                      <button
                        onClick={() => handleChange('custom')}
                        className="text-orange-600 hover:text-orange-800 text-sm"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                )}
                <Calendar
                  selectedDate={value !== 'custom' && value !== 'urgent' && value !== 'this_week' ? value : null}
                  onDateSelect={(date) => handleChange(date)}
                />
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Click on a date to select when you need this completed
                </p>
              </div>
            )}
          </div>
        );

      case 'file-upload':
        return (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-orange-400 transition-colors">
              <input
                type="file"
                multiple={fieldConfig.maxFiles > 1}
                accept={fieldConfig.accept}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > fieldConfig.maxFiles) {
                    alert(`You can only upload up to ${fieldConfig.maxFiles} files`);
                    return;
                  }
                  handleChange(files);
                }}
                className="hidden"
                id={`file-upload-${fieldName}`}
              />
              <label htmlFor={`file-upload-${fieldName}`} className="cursor-pointer">
                <div className="text-4xl mb-3">📎</div>
                <div className="text-sm font-medium text-gray-700 mb-1">
                  Click to upload files
                </div>
                <div className="text-xs text-gray-500">
                  {fieldConfig.accept.includes('image/*') ? 'Images, PDFs, or documents' : 'Files'} 
                  {fieldConfig.maxFiles > 1 && ` (up to ${fieldConfig.maxFiles})`}
                </div>
              </label>
            </div>
            
            {fieldConfig.description && (
              <p className="text-xs text-gray-600">{fieldConfig.description}</p>
            )}
            
            {/* Display uploaded files */}
            {value && value.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Uploaded files:</p>
                {value.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = value.filter((_, i) => i !== index);
                        handleChange(newFiles);
                      }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'conditional-file-upload':
        return (
          <div className="space-y-4">
            {/* First, ask if they have samples */}
            <div>
              <p className="text-sm text-gray-600 mb-3">{fieldConfig.description}</p>
              <div className="space-y-3">
                {fieldConfig.options.map((option, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      if (option.value === 'no') {
                        handleChange({ choice: 'no', files: [] });
                      } else {
                        handleChange({ choice: 'yes', files: [] });
                      }
                    }}
                    className={`w-full flex items-center p-3 sm:p-4 border-2 rounded-xl transition-all text-left touch-manipulation ${
                      value?.choice === option.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <span className="text-xl sm:text-2xl mr-3">
                      {option.value === 'yes' ? '📎' : '✋'}
                    </span>
                    <span className="font-medium text-sm sm:text-base">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Show upload interface only if they chose "yes" */}
            {value?.choice === 'yes' && (
              <div className="space-y-3">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-orange-400 transition-colors">
                  <input
                    type="file"
                    multiple={fieldConfig.maxFiles > 1}
                    accept={fieldConfig.accept}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > fieldConfig.maxFiles) {
                        alert(`You can only upload up to ${fieldConfig.maxFiles} files`);
                        return;
                      }
                      handleChange({ choice: 'yes', files: files });
                    }}
                    className="hidden"
                    id={`file-upload-${fieldName}`}
                  />
                  <label htmlFor={`file-upload-${fieldName}`} className="cursor-pointer">
                    <div className="text-4xl mb-3">📎</div>
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Click to upload files
                    </div>
                    <div className="text-xs text-gray-500">
                      {fieldConfig.accept.includes('image/*') ? 'Images, PDFs, or documents' : 'Files'} 
                      {fieldConfig.maxFiles > 1 && ` (up to ${fieldConfig.maxFiles})`}
                    </div>
                  </label>
                </div>
                
                {/* Display uploaded files */}
                {value.files && value.files.length > 0 && (
                  <>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Uploaded files:</p>
                      {value.files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600 truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newFiles = value.files.filter((_, i) => i !== index);
                              handleChange({ choice: 'yes', files: newFiles });
                            }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Thumbnails for images */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {value.files.map((file, idx) => {
                        let isImage = false;
                        let src = imagePreviews[idx];
                        if (typeof file === 'string') {
                          isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(file);
                        } else if (file instanceof File) {
                          isImage = file.type ? file.type.startsWith('image/') : /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(file.name);
                        }
                        if (!isImage || !src) return null;
                        return (
                          <img
                            key={idx}
                            src={src}
                            alt={`Preview ${file.name || `image-${idx+1}`}`}
                            className="w-16 h-16 object-cover rounded border border-gray-200"
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            placeholder={fieldConfig.placeholder || `Enter ${fieldConfig.label}`}
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            className={`w-full px-3 sm:px-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors ${className}`}
          />
        );
    }
  };

  return (
    <div className="mb-6 sm:mb-8">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {fieldConfig.label} {fieldConfig.required !== false && '*'}
      </label>
      {renderField()}
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default DynamicField; 